<template>
    <a-card :title="$t('screenTitle.paymentHistory')">
        <a-collapse v-if="hasManagerPermission" class="mb-20">
            <a-collapse-panel header="Tìm bằng tên tài khoản">
                <a-form ref="formType" :model="formModelByUName" @finish="onFinishByUName" :scrollToFirstError="true"
                    layout="vertical">
                    <a-form-item :label="$t('labelText.user_name')" name="user_name">
                        <a-input v-model:value="formModelByUName.user_name" />
                    </a-form-item>

                    <a-form-item>
                        <a-button type="primary" html-type="submit" :loading="isLoading">{{ $t('text.search') }}
                        </a-button>
                    </a-form-item>
                </a-form>
            </a-collapse-panel>
        </a-collapse>

        <a-table :dataSource="dataSource" :columns="columns" :pagination="pagination" @change="onChange" :loading="isLoading" :scroll="{x: '100%'}">
            <template #bodyCell="{ column, record, /*text*/ }">
                <template v-if="column.key === 'action'">
                    <a-space>
                        <a-tooltip v-if="hasManagerPermission && (record.status === PaymentStatus.NOT_PAID)" title="Huỷ giao dịch">
                            <a-button type="primary" danger size="small" @click="deletePaymentConfirm(record)">
                                <template #icon>
                                    <DeleteOutlined />
                                </template>
                            </a-button>
                        </a-tooltip>
                    </a-space>
                </template>
            </template>

            <template
                #customFilterDropdown="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }"
            >
                <div style="padding: 8px">
                    <a-input
                        ref="searchInput"
                        :placeholder="`Search ${column.dataIndex}`"
                        :value="selectedKeys"
                        style="width: 188px; margin-bottom: 8px; display: block"
                        @change="(e: any) => setSelectedKeys(e.target.value)"
                        @pressEnter="handleSearch(selectedKeys, confirm, column.dataIndex)"
                    />
                    <a-button
                        type="primary"
                        size="small"
                        style="width: 90px; margin-right: 8px"
                        @click="handleSearch(selectedKeys, confirm, column.dataIndex)"
                    >
                        <template #icon><SearchOutlined /></template>Search
                    </a-button>
                    <a-button size="small" style="width: 90px" @click="handleReset(clearFilters)">Reset</a-button>
                </div>
            </template>
        </a-table>
    </a-card>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, } from "vue";
import { useI18n } from "vue-i18n";

import { Button, Card, Collapse, CollapsePanel, Form, FormItem, Input, message, Modal, Table, TablePaginationConfig, } from "ant-design-vue";
import { SearchOutlined, DeleteOutlined, } from '@ant-design/icons-vue';
import { ColumnType } from "ant-design-vue/lib/table";
import { MenuClickEventHandler, MenuInfo } from "ant-design-vue/lib/menu/src/interface";

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import { UserRole } from "@/store/modules/user";
import { number_format } from "@/utils/helpers";
import { useStore } from "vuex";

export enum PaymentStatus {
    CANCELED_BY_ADMIN = -9,
    FAILED = -1,
    NOT_PAID = 0,
    COMPLETED = 1,
    PROCESSING = 2,
}

export const paymentStatusText = (sts: PaymentStatus): string => {
    switch (sts) {
        case PaymentStatus.CANCELED_BY_ADMIN:
            return 'Bị lỗi hoặc bị huỷ bởi Admin';

        case PaymentStatus.FAILED:
            return 'Đã bị hủy bỏ';

        case PaymentStatus.NOT_PAID:
            return 'Chưa thanh toán';

        case PaymentStatus.COMPLETED:
            return 'Hoàn tất';

        case PaymentStatus.PROCESSING:
            return 'Đang xử lý';

        default:
            return 'Unknown';
    }
};

export enum PaymentType {
    BUY_GAME_CHAR = -2,
    TRANSFER_COIN_TO_GAME = -1,
    NGAN_LUONG = 1,
    ADMIN_CHARGE = 3,
    AFF_RECV = 4,
    CHARGE_ZING_CARD = 5,
    CHARGE_CARD = 6,
    BUY_SHOP_GIFTCODE = 7,
    CHARGE_BAOKIM = 8,
    CHARGE_CARD_BY_PROVIDER = 9,
    CHARGE_BY_CC = 10,
    CHARGE_EBANK_AUTO_BANK = 11,
    CHARGE_CARD_API_V1 = 12,
    CHARGE_CARD_API_V2 = 13,
}

export const paymentTypeText = (type: PaymentType): string => {
    switch (type) {
        case PaymentType.BUY_GAME_CHAR:
            return 'Mua nhân vật game';
        case PaymentType.TRANSFER_COIN_TO_GAME:
            return 'Chuyển tiền vào game';
        case PaymentType.NGAN_LUONG:
            return 'Ngân lượng';
        case PaymentType.ADMIN_CHARGE:
            return 'Nạp tiền thông qua admin';
        case PaymentType.AFF_RECV:
            return 'Nhận tiền thưởng Affiliate';
        case PaymentType.CHARGE_ZING_CARD:
            return 'Nạp thẻ Zing';
        case PaymentType.CHARGE_CARD:
            return 'Nạp thẻ';
        case PaymentType.BUY_SHOP_GIFTCODE:
            return 'Mua Giftcode';
        case PaymentType.CHARGE_BAOKIM:
            return 'Nạp tiền bảo kim';
        case PaymentType.CHARGE_CARD_BY_PROVIDER:
            return 'Nạp thẻ bởi nhà cung cấp';
        case PaymentType.CHARGE_BY_CC:
            return 'Nạp tiền bằng Visa/Credit Card';
        case PaymentType.CHARGE_EBANK_AUTO_BANK:
            return 'Nạp tiền tự động bằng chuyển khoản ngân hàng';
        case PaymentType.CHARGE_CARD_API_V1:
            return 'Nạp thẻ API V1';
        case PaymentType.CHARGE_CARD_API_V2:
            return 'Nạp thẻ API V2';
        default:
            return 'Unknown';
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PaymentModel = any;

export const parseTransInfo = (record: PaymentModel): void => {
    switch (record.type as PaymentType) {
        case PaymentType.TRANSFER_COIN_TO_GAME: {
            const tInfos = record.transaction_info.split(',');
            if (tInfos.length) {
                record.coin = (tInfos.length >= 1) ? -tInfos[1] : 0;
                record.coin_promotion = (tInfos.length > 0) ? -tInfos[0] : record.amount;
                record.point = (tInfos.length > 1) ? tInfos[2] : 0;
            }

            break;
        }

        case PaymentType.ADMIN_CHARGE: {
            const tInfos = record.transaction_info.split(',');
            if (tInfos.length) {
                record.coin = tInfos[1];
                record.coin_promotion = tInfos[2];
                record.point = tInfos[4] || 0;
            }

            break;
        }

        default: {
            record.coin = record.amount;
        }
    }
};

export default defineComponent({
    components: {
        ACard: Card,
        ATable: Table,
        AButton: Button,
        // ASpace: Space,
        AInput: Input,
        AForm: Form,
        AFormItem: FormItem,
        ACollapse: Collapse,
        ACollapsePanel: CollapsePanel,

        // FormOutlined,
        // LinkOutlined,
        SearchOutlined,
        DeleteOutlined,
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();

        const formModelByUName = reactive({
            user_name: '',
        });

        const isLoading = ref(true);
        const dataSource = ref([]);
        const columns = ref<ColumnType[]>([
            {
                title: 'ID',
                dataIndex: 'id',
            },
            {
                title: t('labelText.payment_type'),
                dataIndex: 'type',
                customRender: ({ text }) => paymentTypeText(text),
            },
            {
                title: t('labelText.payment_status'),
                dataIndex: 'status',
                customRender: ({ text }) => paymentStatusText(text),
            },
            {
                title: t('labelText.payment_coin'),
                dataIndex: 'coin',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.payment_coin_promotion'),
                dataIndex: 'coin_promotion',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.payment_point'),
                dataIndex: 'point',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.created_at'),
                dataIndex: "created_at",
            },
            {
                title: t('text.action'),
                key: "action",
            },
        ]);
        const pagination = ref<TablePaginationConfig>({
            current: 1,
        });

        const filters = ref({});

        const fetchData = () => {
            isLoading.value = true;

            ApiRequest.get('/payments', {
                page: pagination.value.current,
                username: formModelByUName.user_name,
                ...filters.value,
            }).then((response) => {
                const resData = response.data;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dataSource.value = resData.data.map((v: any) => {
                    parseTransInfo(v);

                    return v;
                });

                ApiRequest.setPaginate(pagination.value, resData);
            })
            .finally(() => {
                isLoading.value = false;
            });
        };

        // pagination.value.onChange = (page) => {
        //     fetchData(page);
        // };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deleteConfirm = (record: any) => {
            Modal.confirm({
                title: t('message.userListClearConfirmContent'),
                content: record.name,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.del('/pay').then(() => {
                        message.success(t('message.deleteSuccess'));
                    })
                    .finally(() => {
                        fetchData();
                    });
                },
            });
        };

        const switchLock = (record: PaymentModel) => {
            const is_lock = !record.is_lock;
            Modal.confirm({
                title: is_lock ? t('message.userLockConfirm') : t('message.userUnlockConfirm'),
                content: record.name,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.post('/users/' + record.id + '/set-lock', {
                        is_lock: is_lock,
                    }).then(() => {
                        message.success(t('message.updateSuccess'));
                    })
                    .finally(() => {
                        fetchData();
                    });
                },
            });
        };

        const changeRole = (record: PaymentModel): MenuClickEventHandler => (e: MenuInfo) => {
            const roleTo = e.key as UserRole;
            Modal.confirm({
                title: t('message.userSetRoleConfirm'),
                // content: `${record.name}: [${userRoleText(record.role)}] => [${userRoleText(roleTo)}]`,
                autoFocusButton: 'cancel',
                maskClosable: true,
                onOk: async () => {
                    return ApiRequest.post('/users/' + record.id + '/role', {
                        role: roleTo,
                    }).then(() => {
                        message.success(t('message.updateSuccess'));
                    })
                    .finally(() => {
                        fetchData();
                    });
                },
            });
        };

        /* eslint-disable @typescript-eslint/no-explicit-any */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const handleSearch = (selectedKeys: any, confirm: any, dataIndex: any) => {
            confirm();
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleReset = (clearFilters: any) => {
            clearFilters({ confirm: true });
        };

        /* eslint-disable @typescript-eslint/no-explicit-any */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onChange = (_pagination: TablePaginationConfig, fils: any, _sorter: any) => {
            pagination.value = _pagination;

            filters.value = {
                ...fils,
                role: fils.role ? fils.role.join(',') : undefined,
                is_lock: fils.is_lock ? fils.is_lock.join(',') : undefined,
            };

            fetchData();
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const onFinishByUName = (_values: any) => {
            pagination.value.current = 1;

            fetchData();
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const deletePaymentConfirm = (record: any) => {
            Modal.confirm({
                title: t('text.confirm'),
                content: `#${record.id}: ${formModelByUName.user_name || 'You'}`,
                maskClosable: true,
                autoFocusButton: 'cancel',
                onOk: async () => {
                    return ApiRequest.del(`/payments/ebanks/${record.id}`)
                        .then(() => {
                            message.success(t('message.success'));
                        })
                        .finally(() => {
                            fetchData();
                        });
                }
            });
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onMounted(() => {
            fetchData();
        });

        return {
            dataSource,
            columns,
            pagination,
            isLoading,

            fetchData,
            deleteConfirm,
            switchLock,
            changeRole,
            handleReset,
            handleSearch,
            onChange,
            deletePaymentConfirm,

            RouteName,

            formModelByUName,
            onFinishByUName,

            hasManagerPermission: computed<boolean>(() => store.getters.hasManagerPermission),

            UserRole,

            PaymentStatus,
        };
    },
})
</script>

<style scoped>

</style>
